import React from "react";
import ReactMarkdown from "react-markdown";
import { ButtonLink, FigmaIcon, LinkIcon, OpenInNewIcon } from "@aidnas/design-system";
import clsx from "clsx";
import { siteMapping } from "cms/sites";
import NextLink from "next/link";

import { transformChildrenToString } from "./transformChildrenToString";

import styles from "../Styles.module.scss";

function isExternalUrl(href: string): boolean {
  if (!href || href.startsWith("/") || href.startsWith("#")) return false;

  try {
    const targetHostname = new URL(href).hostname;
    const currentHostname = typeof window !== "undefined" ? window.location.hostname : "";

    return targetHostname !== currentHostname && !Object.keys(siteMapping).includes(targetHostname);
  } catch {
    return false;
  }
}

const Anchor = ({
  children,
  href = "",
  ...props
}: React.AnchorHTMLAttributes<HTMLAnchorElement>) => {
  const text = transformChildrenToString(children).trim();
  const isCTA = text.startsWith("!");
  const isSource = text.startsWith("^");
  const cleanText = text.replace(/^[!^]+/, "").trim();
  const hasFigma = /figma/i.test(cleanText);

  if (isCTA) {
    const isExternal = isExternalUrl(href);

    return (
      <NextLink href={href} legacyBehavior passHref>
        <ButtonLink
          href=""
          iconEnd={isExternal ? <OpenInNewIcon /> : undefined}
          iconStart={hasFigma ? <FigmaIcon /> : undefined}
          rel={isExternal ? "noopener noreferrer" : undefined}
          target={isExternal ? "_blank" : undefined}
          variant="subtle"
          {...props}
        >
          {cleanText}
        </ButtonLink>
      </NextLink>
    );
  }

  if (isSource) {
    return (
      <NextLink href={href} legacyBehavior passHref>
        <ButtonLink
          href=""
          iconEnd={<LinkIcon />}
          rel="noopener noreferrer"
          variant="outline"
          {...props}
        >
          {cleanText}
        </ButtonLink>
      </NextLink>
    );
  }

  return (
    <NextLink href={href} passHref {...props} className={styles.link} target="_self">
      {children}
    </NextLink>
  );
};

const Pre = ({ children, className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <pre className={clsx(styles.code_block, className)} {...props}>
    {children}
  </pre>
);

const Code = ({ children, className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <code {...props} className={className}>
    {children}
  </code>
);

const Blockquote = ({ children }: { children?: React.ReactNode }) => (
  <blockquote className={styles.blockquote}>{children}</blockquote>
);

const components = {
  a: Anchor,
  pre: Pre,
  code: Code,
  blockquote: Blockquote,
};

export function MarkdownRenderer({ children }: { children: string }) {
  return <ReactMarkdown components={components}>{children}</ReactMarkdown>;
}
